import { useEffect, useState } from "react";
import { Megaphone, Send } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useWaTemplates } from "@/hooks/admin/useWaTemplates";
import { ConfirmSendModal } from "./ConfirmSendModal";

const inputCls = "bg-white/80 border border-border rounded-full px-4 py-1.5 text-sm outline-none focus:border-accent";

export const PastAnnouncePanel = ({ adminKey, content, setC, onContentSaved, selectedCount, total, sendAnnouncement, announcing, announceJob }) => {
  const { templates, configured } = useWaTemplates(adminKey);
  const [saving, setSaving] = useState(false);
  const name = content?.wa_announce_template_name || "";
  const lang = content?.wa_announce_template_lang || "en";
  const vars = typeof content?.wa_announce_template_vars === "string" ? content.wa_announce_template_vars : "";
  const tpl = templates.find((t) => t.name === name);
  const mapped = vars.split(",").filter((x) => x.trim()).length;

  useEffect(() => {
    if (tpl?.language && tpl.language !== lang) setC("wa_announce_template_lang", tpl.language);
  }, [tpl, lang, setC]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/content`, { method: "PUT", headers: { "Content-Type": "application/json", "X-PL-Client": "web", "X-Admin-Key": adminKey },
        body: JSON.stringify({ wa_announce_template_name: name, wa_announce_template_lang: lang, wa_announce_template_vars: vars }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Failed");
      onContentSaved?.(data);
      toast.success("Announcement template saved");
    } catch (err) {
      toast.error(err.message || "Could not save");
    } finally { setSaving(false); }
  };

  const [pendingSend, setPendingSend] = useState(null);
  const send = (all) => {
    const count = all ? total : selectedCount;
    if (!name) { toast.error("Choose an Announcement template first"); return; }
    if (!count) { toast.error("Select at least one participant"); return; }
    setPendingSend({ all, count });
  };
  const confirmSend = () => {
    const all = pendingSend?.all;
    setPendingSend(null);
    sendAnnouncement({ all, templateName: name, language: lang, variables: vars });
  };
  const preview = tpl?.body ? tpl.body.replace(/{{\s*(\d+)\s*}}/g, (_, i) => `{${vars.split(",")[Number(i) - 1]?.trim() || i}}`) : "";

  return (
    <div className="rounded-2xl border border-[#128C7E]/30 bg-[#F0FAF6] p-4 mb-5" data-testid="past-announce-panel">
      <ConfirmSendModal open={!!pendingSend} count={pendingSend?.count || 0} templateName={name} onCancel={() => setPendingSend(null)} onConfirm={confirmSend} />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p data-testid="past-announce-heading" className="inline-flex items-center gap-2 rounded-md bg-[#1A2E26] text-white px-3 py-1.5 text-sm font-bold tracking-wide shadow-sm border-l-4 border-[#128C7E]"><Megaphone size={16} className="text-[#7FD9C4]" /> II-New batch announcement (WhatsApp only)</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">Tick participants below (or use the header checkbox), choose the Meta-approved Announcement template and send. No email is sent.</p>
        </div>
        {!configured && <span className="text-xs text-red-700" data-testid="past-announce-not-configured">WhatsApp API is not configured on the server.</span>}
      </div>
      <div className="mt-3 grid lg:grid-cols-2 gap-3">
        <div className="space-y-2">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Announcement template</span>
            <select data-testid="past-announce-template" value={templates.some((t) => t.name === name) ? name : ""} onChange={(e) => setC("wa_announce_template_name", e.target.value)} className="mt-1 w-full bg-white border border-border rounded-lg px-2 py-1.5 text-sm outline-none focus:border-accent">
              <option value="">{templates.length ? "— choose an approved template —" : "No approved templates found"}</option>
              {templates.map((t) => <option key={`${t.name}-${t.language}`} value={t.name}>{t.name} · {t.language} · {t.category}</option>)}
            </select>
          </label>
          <div className="flex flex-wrap gap-2 items-end">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Language</span>
              <input data-testid="past-announce-lang" value={lang} onChange={(e) => setC("wa_announce_template_lang", e.target.value)} className={`${inputCls} mt-1 w-24 block`} />
            </label>
            <label className="block flex-1 min-w-[200px]">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Body variables {"{{1}}, {{2}}…"} in order</span>
              <input data-testid="past-announce-vars" value={vars} onChange={(e) => setC("wa_announce_template_vars", e.target.value)} placeholder="e.g. name,batch,url" className={`${inputCls} mt-1 w-full block`} />
            </label>
            <button type="button" data-testid="past-announce-save" onClick={save} disabled={saving} className="rounded-full border border-[#1A2E26] text-[#1A2E26] px-4 py-1.5 text-xs font-medium hover:bg-[#1A2E26] hover:text-white transition-colors disabled:opacity-50">{saving ? "Saving…" : "Save template"}</button>
          </div>
          {tpl && tpl.var_count !== mapped && <p className="text-[11px] text-red-700" data-testid="past-announce-var-mismatch">This template has {tpl.var_count} variable(s) but {mapped} are mapped — fix the mapping so Meta accepts the message.</p>}
          <div className="flex flex-wrap gap-2 pt-1">
            <button type="button" data-testid="past-announce-send-selected" onClick={() => send(false)} disabled={announcing || !configured || !name || !selectedCount} className="inline-flex items-center gap-1.5 rounded-full bg-[#128C7E] text-white px-5 py-2 text-xs font-medium hover:bg-[#0f7568] transition-colors disabled:opacity-50">
              <Send size={12} /> {announcing ? "Sending…" : `Send to selected (${selectedCount})`}
            </button>
            <button type="button" data-testid="past-announce-send-all" onClick={() => send(true)} disabled={announcing || !configured || !name || !total} className="inline-flex items-center gap-1.5 rounded-full border border-[#128C7E] text-[#128C7E] px-5 py-2 text-xs font-medium hover:bg-[#128C7E] hover:text-white transition-colors disabled:opacity-50">
              <Megaphone size={12} /> Send to all ({total})
            </button>
          </div>
          {announceJob && (
            <p className="text-xs text-muted-foreground" data-testid="past-announce-progress">
              {announceJob.done ? "Done" : "Sending"} — {announceJob.sent} sent · {announceJob.failed} failed · of {announceJob.total}
              {announceJob.errors?.length ? <span className="block text-red-700 mt-0.5">{announceJob.errors.slice(-3).map((e) => `${e.name}: ${e.error}`).join(" · ")}</span> : null}
            </p>
          )}
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Preview</p>
          <pre data-testid="past-announce-preview" className="whitespace-pre-wrap font-sans text-xs rounded-2xl rounded-tl-sm bg-[#E7F8EE] border border-[#BFE3CF] px-3 py-2 min-h-[80px] max-h-56 overflow-auto">{preview || "Choose a template to see its message here."}</pre>
          {tpl && <p className="text-[10px] text-muted-foreground mt-1">{tpl.category} · {tpl.language} · {tpl.var_count} variable{tpl.var_count === 1 ? "" : "s"}</p>}
        </div>
      </div>
    </div>
  );
};
